import { useEffect, useRef, useState } from "react";
import { FeatureCollection } from "geojson";
import { fetchWeatherAlerts } from "../WeatherAlertsUtils";
import { useAppDispatch } from "../../../app/hooks";
import {
    setAlertFetchProgress,
    setIsFetchingAlertData,
    setIsNotFetchingAlertData,
    setLatestUpdate
} from "../../../features/alertDisplay/alertDisplaySlice";

export function useWeatherAlerts( autoRefresh: boolean ): FeatureCollection
{
    const [ alerts, setAlerts ] = useState<FeatureCollection>( {
        type: "FeatureCollection",
        features: [],
    } );

    const controllerRef = useRef<AbortController | null>( null );
    const intervalRef = useRef<NodeJS.Timeout | null>( null );
    const dispatch = useAppDispatch();

    useEffect( () =>
    {
        const fetchData = async () =>
        {
            dispatch( setIsFetchingAlertData() );
            if ( controllerRef.current ) controllerRef.current.abort();

            const controller = new AbortController();
            controllerRef.current = controller;

            try
            {
                const result = await fetchWeatherAlerts( controller.signal, ( progress ) =>
                {
                    dispatch( setAlertFetchProgress( progress ) );
                } );

                setAlerts( result );
                dispatch( setIsNotFetchingAlertData() );
                dispatch( setLatestUpdate( Date.now().valueOf() ) );
            }
            catch ( err )
            {
                if ( ( err as Error ).name !== "AbortError" )
                {
                    console.error( "Alert fetch error:", err );
                    dispatch( setIsNotFetchingAlertData() );
                }
            }
        };

        void fetchData();

        if ( autoRefresh )
        {
            intervalRef.current = setInterval( fetchData, 3 * 60 * 1000 );
        }

        return () =>
        {
            if ( intervalRef.current ) clearInterval( intervalRef.current );
            if ( controllerRef.current ) controllerRef.current.abort();
        };
    }, [ autoRefresh ] );

    return alerts;
}
